import type { FormSection, FormSectionProps } from "./form-section-api"

import BankAccountSection from "./BankAccountSection.vue"
import ContactInformationSection from "./ContactInformationSection.vue"
import EmployerInformationSection from "./EmployerInformationSection.vue"
import HomeAddressSection, {
  HomeAddressPrimaryFields,
  HomeAddressJointFields,
} from "./HomeAddressSection.vue"
import IdentitySection from "./IdentityInformationSection.vue"
import IncomeSection from "./IncomeSection.vue"
import PersonalInformationSection from "./PersonalInformationSection.vue"
import PurchaseInformationSection from "./PurchaseInformationSection.vue"
import BankCardSection from "./BankCardSection.vue"
import SpouseInformationSection from "./SpouseInformationSection.vue"
import PreferenceSection from "./PreferenceSection.vue"
import ReferenceInformationSection from "./ReferenceInformationSection.vue"
import JointApplicantSelectionSection from "./JointApplicantSelectionSection.vue"

export type { FormSection, FormSectionProps }

export const PurchaseInformation: FormSection = {
  id: "purchase-information",
  fields: ["purchaseAmountCents"],
  type: "purchase",
  component: PurchaseInformationSection,
}

export const PrimaryPersonalInformation: FormSection = {
  id: "primary-personal-information",
  type: "personal",
  fields: [
    "primary.firstName",
    "primary.middleInitial",
    "primary.lastName",
    "primary.dateOfBirth",
    "primary.ssn",
  ],
  component: PersonalInformationSection,
}

export const PrimaryHomeAddress: FormSection = {
  id: "primary-home-address",
  type: "address",
  fields: HomeAddressPrimaryFields,
  component: HomeAddressSection,
}

export const PrimaryContactInformation: FormSection = {
  id: "primary-contact-information",
  type: "contact",
  fields: [
    "primary.homePhone",
    "primary.mobilePhone",
    "primary.workPhone",
    "primary.email",
    "primary.useEmailForAllCorrespondence",
  ],
  component: ContactInformationSection,
}

export const PrimaryIdentityInformation: FormSection = {
  id: "primary-identity-information",
  type: "identity",
  fields: [
    "primary.identity.type",
    "primary.identity.number",
    "primary.identity.state",
    "primary.identity.expirationDate",
  ],
  component: IdentitySection,
}

export const PrimaryIncomeInformation: FormSection = {
  id: "primary-income-information",
  type: "income",
  fields: [
    "primary.income.netMonthlyCents",
    "primary.income.grossMonthlyCents",
    "primary.income.grossAnnualCents",
    "primary.income.type",
    "primary.income.directDeposit",
    "primary.income.payFrequency",
    "primary.income.previousPayDate",
    "primary.income.nextPayDate",
  ],
  component: IncomeSection,
}

export const PrimaryBankAccountInformation: FormSection = {
  id: "primary-bank-account-information",
  type: "income",
  fields: [
    "bankAccount.bankName",
    "bankAccount.autoPay",
    "bankAccount.routingNumber",
    "bankAccount.accountNumber",
  ],
  component: BankAccountSection,
}

export const PrimaryBankCardInformation: FormSection = {
  id: "primary-bank-card-Information",
  type: "income",
  fields: [
    "bankCard.cardholderFirstName",
    "bankCard.cardholderLastName",
    "bankCard.cardNumber",
    "bankCard.cardExpirationMonth",
    "bankCard.cardExpirationYear",
    "bankCard.address.line1",
    "bankCard.address.line2",
    "bankCard.address.city",
    "bankCard.address.state",
    "bankCard.address.zipCode",
    "paymentMethod",
  ],
  component: BankCardSection,
}

export const PrimaryPreferenceInformation: FormSection = {
  id: "primary-preference-information",
  type: "additional",
  fields: ["primary.preferredLanguage"],
  component: PreferenceSection,
}

export const PrimarySpouseInformation: FormSection = {
  id: "primary-spouse-information",
  type: "additional",
  fields: [
    "primary.maritalStatus",
    "primary.spouse.firstName",
    "primary.spouse.lastName",
    "primary.spouse.liveWithSpouse",
    "primary.spouse.homeAddress.line1",
    "primary.spouse.homeAddress.line2",
    "primary.spouse.homeAddress.city",
    "primary.spouse.homeAddress.state",
    "primary.spouse.homeAddress.zipCode",
    "primary.spouse.email",
  ],
  component: SpouseInformationSection,
}

export const PrimaryEmployerInformation: FormSection = {
  id: "primary-employer-information",
  type: "income",
  fields: [
    "primary.employment.employerName",
    "primary.employment.jobTitle",
    "primary.employment.employerPhone",
    "primary.employment.employerPhoneExtension",
  ],
  component: EmployerInformationSection,
}

export const PrimaryReferenceInformation: FormSection = {
  id: "primary-reference-information",
  type: "contact",
  fields: [
    "primary.reference.firstName",
    "primary.reference.lastName",
    "primary.reference.phoneNumber",
    "primary.reference.relationship",
  ],
  component: ReferenceInformationSection,
}

export const PrimaryJointApplicantSelection: FormSection = {
  id: "primary-joint-applicant-selection",
  type: "contact",
  fields: ["jointEnabled"],
  component: JointApplicantSelectionSection,
}

export const JointPersonalInformation: FormSection = {
  id: "joint-personal-information",
  type: "personal",
  fields: [
    "joint.firstName",
    "joint.middleInitial",
    "joint.lastName",
    "joint.dateOfBirth",
    "joint.ssn",
  ],
  component: PersonalInformationSection,
}

export const JointHomeAddress: FormSection = {
  id: "joint-home-address",
  type: "address",
  fields: HomeAddressJointFields,
  component: HomeAddressSection,
}

export const JointContactInformation: FormSection = {
  id: "joint-contact-information",
  type: "contact",
  fields: [
    "joint.homePhone",
    "joint.mobilePhone",
    "joint.workPhone",
    "joint.email",
  ],
  component: ContactInformationSection,
}
export const JointIncomeInformation: FormSection = {
  id: "joint-income-information",
  type: "income",
  fields: [
    "joint.income.netMonthlyCents",
    "joint.income.grossMonthlyCents",
    "joint.income.type",
    "joint.income.directDeposit",
    "joint.income.payFrequency",
    "joint.income.previousPayDate",
    "joint.income.nextPayDate",
  ],
  component: IncomeSection,
}
